import { useQuery } from '@apollo/client';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  ButtonLink,
  Heading,
  NavigationTabs,
  NavigationTabsItem,
  Stack,
  Text,
} from '@sixfold/common-ui';
import React from 'react';
import { Navigate, Route, Routes as RouterRoutes, useParams } from 'react-router';

import { Insights } from './insights';
import { ToursTable, toursViewSearch } from './tours_table';
import { Card, StatCard } from './ui';
import { CompanyFeatureFlagsContainer } from '../../company/containers/company_feature_flags_container';
import { MobileCompanyNameQuery, MobileCompanyNameQueryVariables } from '../../lib/graphql';
import { Routes } from '../../routes';
import { mobileCompanyNameQuery } from '../graphql';
import { EMPTY_STATS, MOBILE_FEATURE_FLAGS, MOBILE_FLAG_LABEL, STATS_WINDOW_DAYS, useMobileData } from '../hooks';

import styles from './mobile.module.css';

export const CompanyPage: React.FC = () => {
  const { company_id } = useParams() as { company_id: string };
  const { companies, statsByCompany, loading } = useMobileData();
  const company = companies.find((candidate) => candidate.companyId === company_id);
  const stats = statsByCompany.get(company_id) ?? EMPTY_STATS;
  const nameResult = useQuery<MobileCompanyNameQuery, MobileCompanyNameQueryVariables>(mobileCompanyNameQuery, {
    variables: { company_id },
    skip: loading || company !== undefined,
  });
  const name = company?.companyName ?? nameResult.data?.company?.company_name ?? company_id;
  const base = Routes.MobileCompany.generatePath({ company_id });
  const toursPath = `${base}/tours`;
  const percent = (part: number) => (stats.mobile === 0 ? '-' : `${Math.round((part / stats.mobile) * 100)}%`);

  return (
    <Stack direction="column" wrap={false} gap="medium">
      <Breadcrumbs label="Breadcrumbs">
        <BreadcrumbsItem href={Routes.MobileCommandCenter.generatePath({})}>Mobile Command Center</BreadcrumbsItem>
        <BreadcrumbsItem href={Routes.MobileCompanies.generatePath({})}>Companies</BreadcrumbsItem>
        <BreadcrumbsItem current="page">{name}</BreadcrumbsItem>
      </Breadcrumbs>
      <Stack justifyContent="space-between" alignItems="start" gap="medium">
        <Stack direction="column" wrap={false} gap="xsmall">
          <Heading type="title" size="large">
            {name}
          </Heading>
          <Text prominence="subtle">
            {loading || company !== undefined
              ? (company?.enabledFlags.map((flag) => MOBILE_FLAG_LABEL[flag]).join(' · ') ?? '')
              : 'No mobile transports in this window'}
          </Text>
        </Stack>
        <ButtonLink
          kind="neutral"
          size="small"
          iconStart="small-company"
          href={Routes.Company.generatePath({ company_id })}>
          View company
        </ButtonLink>
      </Stack>

      <div className={styles.kpiGrid}>
        <StatCard
          label="Mobile transports"
          value={stats.mobile}
          loading={loading}
          hint={`Last ${STATS_WINDOW_DAYS} days`}
          pathname={toursPath}
        />
        <StatCard
          label="Completed"
          value={stats.completed}
          loading={loading}
          tone="success"
          pathname={toursPath}
          search={toursViewSearch({ status: 'completed' })}
        />
        <StatCard
          label="Failed"
          value={stats.failed}
          loading={loading}
          tone={stats.failed > 0 ? 'danger' : 'default'}
          pathname={toursPath}
          search={toursViewSearch({ status: 'failed' })}
        />
        <StatCard
          label="Ongoing"
          value={stats.ongoing}
          loading={loading}
          pathname={toursPath}
          search={toursViewSearch({ status: 'ongoing' })}
        />
        <StatCard
          label="Needs action"
          value={stats.needsAction}
          loading={loading}
          tone={stats.needsAction > 0 ? 'danger' : 'success'}
          hint="Failing or with warnings"
          pathname={toursPath}
          search={toursViewSearch({ health: 'action' })}
        />
        <StatCard
          label="Positions received"
          value={stats.tracked}
          loading={loading}
          hint={`${percent(stats.tracked)} of mobile`}
          pathname={toursPath}
          search={toursViewSearch({ positions: 'received' })}
        />
      </div>

      <NavigationTabs>
        <NavigationTabsItem href={toursPath}>Tours{loading ? '' : ` (${stats.mobile})`}</NavigationTabsItem>
        <NavigationTabsItem href={`${base}/setup`}>Feature flags</NavigationTabsItem>
        <NavigationTabsItem href={`${base}/insights`}>Insights</NavigationTabsItem>
      </NavigationTabs>
      <RouterRoutes>
        <Route path="tours" element={<ToursTable carrierId={company_id} />} />
        <Route
          path="setup"
          element={
            <Card title="Mobile feature flags" subtitle="Toggle flags and review who changed them in the audit log">
              <CompanyFeatureFlagsContainer onlyFlagIds={MOBILE_FEATURE_FLAGS} />
            </Card>
          }
        />
        <Route path="insights" element={<Insights companyId={company_id} />} />
        <Route index element={<Navigate to={`${base}/tours`} replace />} />
      </RouterRoutes>
    </Stack>
  );
};
